import axios from 'axios';
import { sessionGet, sessionSet, sessionRemove, persistSessionBackup, clearSessionBackup } from '../utils/sessionStore';

// console.log("api.ts: Creating axios instance");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 30000,
});

// console.log("api.ts: Setting up request interceptor");

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // console.log("api.ts: Request interceptor - processing request to:", config.url);
    // Use ONLY this tab's own sessionStorage token. Never fall back to the
    // shared localStorage backup here — that backup holds the LATEST login
    // across all tabs, so pulling it mid-session let a tab still signed in as
    // account A silently adopt account B (opened in another tab) and show B's
    // data. A brand-new tab already gets its token synchronously at startup
    // (restoreSessionBackup runs at module load in sessionStore.ts).
    const token = sessionGet('accessToken');
    if (token) {
      // console.log("api.ts: Request interceptor - adding auth token to request");
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // console.log("api.ts: Request interceptor - no auth token found");
    }
    // console.log("api.ts: Request interceptor - final config:", {
    //   url: config.url,
    //   method: config.method,
    //   headers: config.headers,
    //   data: config.data
    // });
    return config;
  },
  (error) => {
    console.error("api.ts: Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// console.log("api.ts: Setting up response interceptor");

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    // console.log("api.ts: Response interceptor - successful response from:", response.config.url);
    // console.log("api.ts: Response interceptor - response data:", response.data);
    return response;
  },
  async (error) => {
    console.error("api.ts: Response interceptor - error occurred:", error);
    console.error("api.ts: Response interceptor - error response:", error.response);
    
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // console.log("api.ts: Response interceptor - 401 error, attempting token refresh");
      originalRequest._retry = true;

      try {
        const refreshToken = sessionGet('refreshToken');
        if (refreshToken) {
          // console.log("api.ts: Response interceptor - attempting to refresh token");
          // Use bare axios (not `api`) so this call skips the request/response
          // interceptors — that avoids a refresh->401->refresh loop and sending a
          // stale Authorization header. But it must still hit the API origin, so
          // prepend the same baseURL the `api` instance uses.
          const response = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/refresh`,
            { refreshToken }
          );

          const { accessToken } = response.data;
          // console.log("api.ts: Response interceptor - token refresh successful");
          sessionSet('accessToken', accessToken);
          // Keep the persisted (localStorage) backup's token fresh too.
          persistSessionBackup();

          // Retry the original request
          // console.log("api.ts: Response interceptor - retrying original request");
          return api(originalRequest);
        } else {
          // console.log("api.ts: Response interceptor - no refresh token available");
        }
      } catch (refreshError) {
        // console.error("api.ts: Response interceptor - token refresh failed:", refreshError);
        // to login or handle refresh failure
        // Drop the persisted backup for this dead session so a reopen doesn't
        // restore it into a refresh->401 loop.
        clearSessionBackup(sessionGet('accessToken'));
        sessionRemove('accessToken');
        sessionRemove('refreshToken');
        // console.log("api.ts: Response interceptor - tokens cleared, redirecting to login");
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// console.log("api.ts: Axios instance configured and ready");

export default api;